import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarClock, MapPin, MessageSquare, User, Star, Loader2, Download, RotateCw, XCircle, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { DashboardShell, StatCard, StatusBadge } from "@/components/dashboard/DashboardShell";
import { Stars } from "@/components/Stars";
import { api } from "@/lib/api";
import { gbp } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { key: "overview", label: "Dashboard", icon: LayoutDashboard },
  { key: "bookings", label: "My Bookings", icon: CalendarClock },
  { key: "saved", label: "Saved Locations", icon: MapPin },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "profile", label: "Profile", icon: User },
];

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rateFor, setRateFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [rtext, setRtext] = useState("");

  const load = () => { setLoading(true); api.get("/my/bookings").then(({ data }) => setBookings(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); if (["messages"].includes(active)) navigate("/messages"); }, [active]);

  const upcoming = bookings.filter((b) => !["completed", "cancelled", "no_show"].includes(b.status));
  const completed = bookings.filter((b) => b.status === "completed");
  const spent = completed.reduce((s, b) => s + b.total, 0);

  const cancel = async (id) => { await api.post(`/bookings/${id}/cancel`); toast.success("Booking cancelled"); load(); };
  const rebook = (b) => { navigate("/search"); };
  const submitRate = async () => { await api.post(`/bookings/${rateFor.id}/rate`, { rating, text: rtext }); toast.success("Thanks for your feedback!"); setRateFor(null); setRtext(""); load(); };

  const BookingRow = ({ b }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`booking-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><span className="font-heading font-semibold text-navy">{b.booking_ref}</span><StatusBadge status={b.status} /></div>
          <p className="mt-1 text-sm text-slate-600">{b.pickup?.name}{b.dropoff ? ` → ${b.dropoff.name}` : ""}</p>
          <p className="mt-0.5 text-xs text-slate-400">{b.date} · {b.time} · {b.vehicle_label} · {b.driver_name || "Awaiting driver"}</p>
        </div>
        <span className="font-heading text-lg font-bold text-navy">{gbp(b.total)}</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => navigate(`/confirmation/${b.booking_ref}`)} data-testid={`view-${b.id}`}>View</Button>
        {["driver_assigned", "driver_accepted", "driver_enroute", "driver_arrived", "picked_up", "journey_started"].includes(b.status) && <Button size="sm" className="gap-1 bg-brand hover:bg-brand/90" onClick={() => navigate(`/track/${b.id}`)} data-testid={`track-${b.id}`}><Navigation size={13} /> Track</Button>}
        {b.payment_status === "pending" && b.status === "pending" && <Button size="sm" className="bg-brand hover:bg-brand/90" onClick={() => navigate(`/checkout/${b.id}`)} data-testid={`pay-${b.id}`}>Complete Payment</Button>}
        <Button size="sm" variant="outline" className="gap-1" onClick={() => navigate("/messages")}><MessageSquare size={13} /> Message</Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => toast.success("Invoice downloaded (demo)")}><Download size={13} /> Invoice</Button>
        {b.status === "completed" && !b.rating && <Button size="sm" variant="outline" className="gap-1" onClick={() => setRateFor(b)} data-testid={`rate-${b.id}`}><Star size={13} /> Rate</Button>}
        {b.status === "completed" && <Button size="sm" variant="outline" className="gap-1" onClick={() => rebook(b)}><RotateCw size={13} /> Rebook</Button>}
        {!["completed", "cancelled"].includes(b.status) && <Button size="sm" variant="ghost" className="gap-1 text-red-600" onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}><XCircle size={13} /> Cancel</Button>}
      </div>
    </div>
  );

  return (
    <DashboardShell title="Customer" nav={NAV} active={active} onNav={(k) => k === "messages" ? navigate("/messages") : setActive(k)}>
      {loading ? <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div> : (
        <>
          {active === "overview" && (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard icon={CalendarClock} label="Upcoming journeys" value={upcoming.length} />
                <StatCard icon={LayoutDashboard} label="Total bookings" value={bookings.length} color="navy" />
                <StatCard icon={Star} label="Completed trips" value={completed.length} color="emerald" />
                <StatCard icon={User} label="Total spent" value={gbp(spent)} color="amber" />
              </div>
              <div>
                <h2 className="mb-3 font-heading text-lg font-bold text-navy">Upcoming journey</h2>
                {upcoming[0] ? <BookingRow b={upcoming[0]} /> : <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">No upcoming journeys. <Button variant="link" className="text-brand" onClick={() => navigate("/search")}>Book a taxi</Button></div>}
              </div>
              <div>
                <h2 className="mb-3 font-heading text-lg font-bold text-navy">Recent bookings</h2>
                <div className="grid gap-3">{bookings.slice(0, 3).map((b) => <BookingRow key={b.id} b={b} />)}</div>
              </div>
            </div>
          )}
          {active === "bookings" && (
            <div className="grid gap-3">
              {bookings.length === 0 ? <p className="text-sm text-slate-500">No bookings yet.</p> : bookings.map((b) => <BookingRow key={b.id} b={b} />)}
            </div>
          )}
          {active === "saved" && <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Save your home, work and favourite places for one-tap booking. <br />No saved locations yet.</div>}
          {active === "profile" && <ProfileCard />}
        </>
      )}

      <Dialog open={!!rateFor} onOpenChange={(o) => !o && setRateFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">How was your journey?</DialogTitle></DialogHeader>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => <button key={i} onClick={() => setRating(i)} data-testid={`star-${i}`}><Star size={32} className={i <= rating ? "fill-gold text-gold" : "text-slate-300"} /></button>)}
          </div>
          <Textarea value={rtext} onChange={(e) => setRtext(e.target.value)} placeholder="Tell us about your driver and vehicle" data-testid="rate-text" />
          <DialogFooter><Button className="bg-brand hover:bg-brand/90" onClick={submitRate} data-testid="submit-rating">Submit Rating</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

function ProfileCard() {
  const { user } = useAuth();
  return (
    <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand text-xl font-bold text-white">{user?.name?.[0]}</span>
        <div><p className="font-heading text-lg font-semibold text-navy">{user?.name}</p><p className="text-sm text-slate-500">{user?.email}</p></div>
      </div>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-500">Phone</span><span className="font-medium">{user?.phone || "—"}</span></div>
        <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-500">Role</span><span className="font-medium capitalize">{user?.role}</span></div>
      </div>
    </div>
  );
}
